import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Megaphone, Plus, Trash2, Paperclip, ChevronDown, ChevronRight, Download,
  Calendar as CalIcon, Radio, Mail, PartyPopper, PhoneCall, Store, ChevronLeft, Pencil,
  ArrowUp, ArrowDown,
} from "lucide-react";
import { toast } from "sonner";

const ACTIVITY_TYPES = [
  { value: "webinar",        label: "Webinar",        icon: Radio,       color: "bg-blue-100 text-blue-800 border-blue-200" },
  { value: "email_campaign", label: "Email Campaign", icon: Mail,        color: "bg-violet-100 text-violet-800 border-violet-200" },
  { value: "event",          label: "Event",          icon: PartyPopper, color: "bg-amber-100 text-amber-800 border-amber-200" },
  { value: "call_out_day",   label: "Call-Out Day",   icon: PhoneCall,   color: "bg-rose-100 text-rose-800 border-rose-200" },
  { value: "tradeshow",      label: "Tradeshow",      icon: Store,       color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
];
const typeMeta = (t) => ACTIVITY_TYPES.find((x) => x.value === t) || ACTIVITY_TYPES[0];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const activityTimeLabel = (a) => {
  if (!a || a.all_day || !a.start_time) return "";
  return a.end_time ? `${a.start_time}–${a.end_time}` : a.start_time;
};

export default function Marketing() {
  return (
    <div className="space-y-6" data-testid="marketing-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-blue-700 flex items-center gap-1.5">
          <Megaphone className="h-3 w-3" /> Marketing
        </div>
        <h1 className="font-heading text-3xl font-bold text-slate-900 mt-1">Marketing</h1>
        <p className="text-sm text-slate-500 mt-1">
          Plan campaigns, manage tasks and see every marketing activity on one calendar.
        </p>
      </div>

      <Tabs defaultValue="projects">
        <TabsList>
          <TabsTrigger value="projects" data-testid="marketing-tab-projects">Projects &amp; tasks</TabsTrigger>
          <TabsTrigger value="calendar" data-testid="marketing-tab-calendar">Calendar</TabsTrigger>
        </TabsList>

        <TabsContent value="projects" className="mt-6"><ProjectsPanel /></TabsContent>
        <TabsContent value="calendar" className="mt-6"><CalendarPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

// -----------------------------------------------------------------------------
// PROJECTS & TASKS
// -----------------------------------------------------------------------------
function ProjectsPanel() {
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", owner_id: "" });
  const [expanded, setExpanded] = useState({});
  const [taskDraft, setTaskDraft] = useState({}); // { [pid]: { title, ... } }
  const [editingProject, setEditingProject] = useState(null); // project obj or null
  const [editingTask, setEditingTask] = useState(null);       // { pid, task } or null

  const reload = async () => {
    const [p, u] = await Promise.all([api.get("/marketing/projects"), api.get("/users")]);
    setProjects(p.data); setUsers(u.data);
  };
  useEffect(() => { reload(); }, []);

  const createProject = async () => {
    if (!form.name.trim()) return toast.error("Project name required");
    try {
      await api.post("/marketing/projects", {
        name: form.name.trim(),
        description: form.description.trim(),
        owner_id: form.owner_id || null,
      });
      setForm({ name: "", description: "", owner_id: "" });
      setCreating(false);
      await reload();
      toast.success("Project created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveProject = async () => {
    if (!editingProject) return;
    if (!editingProject.name?.trim()) return toast.error("Project name required");
    try {
      await api.patch(`/marketing/projects/${editingProject.id}`, {
        name: editingProject.name.trim(),
        description: (editingProject.description || "").trim(),
        owner_id: editingProject.owner_id || null,
      });
      setEditingProject(null);
      await reload();
      toast.success("Project updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const saveTask = async () => {
    if (!editingTask) return;
    const { pid, task } = editingTask;
    if (!task.title?.trim()) return toast.error("Task title required");
    try {
      await api.patch(`/marketing/projects/${pid}/tasks/${task.id}`, {
        title: task.title.trim(),
        description: (task.description || "").trim(),
        owner_id: task.owner_id || null,
        target_delivery_date: task.target_delivery_date || null,
      });
      setEditingTask(null);
      await reload();
      toast.success("Task updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeProject = async (pid) => {
    if (!window.confirm("Delete this project and all its tasks?")) return;
    try { await api.delete(`/marketing/projects/${pid}`); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  const addTask = async (pid) => {
    const d = taskDraft[pid] || {};
    if (!d.title?.trim()) return toast.error("Task title required");
    try {
      await api.post(`/marketing/projects/${pid}/tasks`, {
        title: d.title.trim(),
        description: (d.description || "").trim(),
        owner_id: d.owner_id || null,
        target_delivery_date: d.target_delivery_date || null,
      });
      setTaskDraft((s) => ({ ...s, [pid]: {} }));
      await reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const removeTask = async (pid, tid) => {
    if (!window.confirm("Delete task?")) return;
    try { await api.delete(`/marketing/projects/${pid}/tasks/${tid}`); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  const setTaskStatus = async (pid, tid, status) => {
    try { await api.patch(`/marketing/projects/${pid}/tasks/${tid}`, { status }); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  const reorderTask = async (pid, tid, direction) => {
    // Compute the new order locally first so the UI feels snappy, then
    // persist. If the server fails we reload to restore ground truth.
    const proj = projects.find((p) => p.id === pid);
    if (!proj) return;
    const ids = (proj.tasks || []).map((t) => t.id);
    const idx = ids.indexOf(tid);
    if (idx === -1) return;
    const swap = direction === "up" ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= ids.length) return;
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    try {
      await api.post(`/marketing/projects/${pid}/tasks/reorder`, { task_ids: ids });
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
      await reload();
    }
  };

  const uploadAttachment = async (pid, tid, files) => {
    if (!files || files.length === 0) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        await api.post(`/marketing/projects/${pid}/tasks/${tid}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      await reload();
      toast.success("Uploaded");
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
  };

  const removeAttachment = async (pid, tid, aid) => {
    if (!window.confirm("Remove attachment?")) return;
    try { await api.delete(`/marketing/projects/${pid}/tasks/${tid}/attachments/${aid}`); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="marketing-projects">
      <div className="flex justify-between items-center">
        <div className="text-xs text-slate-500">{projects.length} project{projects.length === 1 ? "" : "s"}</div>
        <Button onClick={() => setCreating(true)} data-testid="new-marketing-project" className="bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4 mr-1" /> New project
        </Button>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader><DialogTitle>New marketing project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Name *</label>
              <Input data-testid="marketing-project-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Q4 Cyber Awareness Campaign" />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Description</label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </div>
            <OwnerSelect users={users} value={form.owner_id} onChange={(v) => setForm({ ...form, owner_id: v })} testid="marketing-project-owner" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button onClick={createProject} data-testid="marketing-project-save" className="bg-blue-600 hover:bg-blue-700">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {projects.length === 0 ? (
        <Card className="border-dashed"><CardContent className="p-10 text-center text-sm text-slate-500">
          No projects yet. Click <b>New project</b> to get started.
        </CardContent></Card>
      ) : projects.map((p) => {
        const open = !!expanded[p.id];
        const draft = taskDraft[p.id] || {};
        const done = (p.tasks || []).filter((t) => t.status === "done").length;
        const total = (p.tasks || []).length;
        return (
          <Card key={p.id} className="border-slate-200" data-testid={`marketing-project-${p.id}`}>
            <CardHeader className="border-b border-slate-100 py-3">
              <div className="flex items-center justify-between gap-3">
                <button className="flex items-center gap-2 text-left" onClick={() => setExpanded((s) => ({ ...s, [p.id]: !open }))}>
                  {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
                  <CardTitle className="text-base font-heading">{p.name}</CardTitle>
                  <Badge variant="outline" className="text-[10px]">{done}/{total} done</Badge>
                  {p.owner_name && <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-200 text-blue-700">{p.owner_name}</Badge>}
                </button>
                <div className="flex items-center gap-0.5">
                  <Button variant="ghost" size="icon" onClick={() => setEditingProject({ id: p.id, name: p.name, description: p.description || "", owner_id: p.owner_id || "" })} data-testid={`edit-project-${p.id}`}>
                    <Pencil className="h-4 w-4 text-slate-400 hover:text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => removeProject(p.id)} data-testid={`delete-project-${p.id}`}>
                    <Trash2 className="h-4 w-4 text-slate-400 hover:text-rose-600" />
                  </Button>
                </div>
              </div>
              {p.description && <div className="text-xs text-slate-500 mt-1 ml-6">{p.description}</div>}
            </CardHeader>

            {open && (
              <CardContent className="p-5 space-y-4">
                <div className="space-y-2">
                  {(p.tasks || []).length === 0 ? (
                    <div className="text-xs text-slate-400 text-center py-3">No tasks yet.</div>
                  ) : (
                    p.tasks.map((t, idx) => (
                      <TaskRow
                        key={t.id}
                        pid={p.id}
                        task={t}
                        users={users}
                        isFirst={idx === 0}
                        isLast={idx === (p.tasks.length - 1)}
                        onMoveUp={() => reorderTask(p.id, t.id, "up")}
                        onMoveDown={() => reorderTask(p.id, t.id, "down")}
                        onEdit={() => setEditingTask({ pid: p.id, task: { ...t } })}
                        onDelete={() => removeTask(p.id, t.id)}
                        onStatus={(s) => setTaskStatus(p.id, t.id, s)}
                        onUpload={(files) => uploadAttachment(p.id, t.id, files)}
                        onRemoveAtt={(aid) => removeAttachment(p.id, t.id, aid)}
                      />
                    ))
                  )}
                </div>

                {/* New task inline form */}
                <div className="rounded border border-dashed border-slate-300 bg-slate-50 p-3">
                  <div className="text-[10px] font-mono uppercase text-slate-500 mb-2">Add a task</div>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
                    <Input className="md:col-span-4" data-testid={`new-task-title-${p.id}`} placeholder="Task title *" value={draft.title || ""} onChange={(e) => setTaskDraft((s) => ({ ...s, [p.id]: { ...draft, title: e.target.value } }))} />
                    <Input className="md:col-span-4" placeholder="Description" value={draft.description || ""} onChange={(e) => setTaskDraft((s) => ({ ...s, [p.id]: { ...draft, description: e.target.value } }))} />
                    <div className="md:col-span-2">
                      <OwnerSelect users={users} value={draft.owner_id || ""} onChange={(v) => setTaskDraft((s) => ({ ...s, [p.id]: { ...draft, owner_id: v } }))} compact />
                    </div>
                    <Input className="md:col-span-1" type="date" value={draft.target_delivery_date || ""} onChange={(e) => setTaskDraft((s) => ({ ...s, [p.id]: { ...draft, target_delivery_date: e.target.value } }))} />
                    <Button className="md:col-span-1 bg-blue-600 hover:bg-blue-700" data-testid={`add-task-${p.id}`} onClick={() => addTask(p.id)}>
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            )}
          </Card>
        );
      })}

      {/* Edit project dialog */}
      <Dialog open={!!editingProject} onOpenChange={(v) => !v && setEditingProject(null)}>
        <DialogContent data-testid="edit-marketing-project-dialog">
          <DialogHeader><DialogTitle>Edit project</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Name *</label>
              <Input data-testid="edit-marketing-project-name" value={editingProject?.name || ""} onChange={(e) => setEditingProject((s) => ({ ...s, name: e.target.value }))} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Description</label>
              <Textarea rows={3} value={editingProject?.description || ""} onChange={(e) => setEditingProject((s) => ({ ...s, description: e.target.value }))} />
            </div>
            <OwnerSelect users={users} value={editingProject?.owner_id || ""} onChange={(v) => setEditingProject((s) => ({ ...s, owner_id: v }))} testid="edit-marketing-project-owner" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingProject(null)}>Cancel</Button>
            <Button onClick={saveProject} data-testid="edit-marketing-project-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit task dialog */}
      <Dialog open={!!editingTask} onOpenChange={(v) => !v && setEditingTask(null)}>
        <DialogContent data-testid="edit-marketing-task-dialog">
          <DialogHeader><DialogTitle>Edit task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Title *</label>
              <Input data-testid="edit-marketing-task-title" value={editingTask?.task?.title || ""} onChange={(e) => setEditingTask((s) => ({ ...s, task: { ...s.task, title: e.target.value } }))} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Description</label>
              <Textarea rows={3} value={editingTask?.task?.description || ""} onChange={(e) => setEditingTask((s) => ({ ...s, task: { ...s.task, description: e.target.value } }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <OwnerSelect users={users} value={editingTask?.task?.owner_id || ""} onChange={(v) => setEditingTask((s) => ({ ...s, task: { ...s.task, owner_id: v } }))} testid="edit-marketing-task-owner" />
              <div>
                <label className="text-[10px] font-mono uppercase text-slate-500">Target delivery</label>
                <Input type="date" data-testid="edit-marketing-task-date" value={editingTask?.task?.target_delivery_date || ""} onChange={(e) => setEditingTask((s) => ({ ...s, task: { ...s.task, target_delivery_date: e.target.value } }))} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditingTask(null)}>Cancel</Button>
            <Button onClick={saveTask} data-testid="edit-marketing-task-save" className="bg-blue-600 hover:bg-blue-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function TaskRow({ pid, task, users, isFirst, isLast, onMoveUp, onMoveDown, onEdit, onDelete, onStatus, onUpload, onRemoveAtt }) {
  const [open, setOpen] = useState(false);
  const statusStyles = {
    todo: "bg-slate-100 text-slate-700 border-slate-200",
    in_progress: "bg-amber-100 text-amber-800 border-amber-200",
    done: "bg-emerald-100 text-emerald-800 border-emerald-200",
  };
  return (
    <div className="rounded border border-slate-200 p-3" data-testid={`task-row-${task.id}`}>
      <div className="flex items-start gap-3">
        <button className="mt-0.5" onClick={() => setOpen((o) => !o)}>
          {open ? <ChevronDown className="h-4 w-4 text-slate-500" /> : <ChevronRight className="h-4 w-4 text-slate-500" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-slate-900 text-sm">{task.title}</span>
            <Badge variant="outline" className={`text-[10px] ${statusStyles[task.status] || ""}`}>{(task.status || "todo").replace("_", " ")}</Badge>
            {task.target_delivery_date && (
              <Badge variant="outline" className="text-[10px] bg-blue-50 border-blue-200 text-blue-700">Due {fmtDate(task.target_delivery_date)}</Badge>
            )}
            {task.owner_name && <Badge variant="outline" className="text-[10px]">{task.owner_name}</Badge>}
            {(task.attachments || []).length > 0 && (
              <Badge variant="outline" className="text-[10px]"><Paperclip className="h-2.5 w-2.5 mr-0.5" />{(task.attachments || []).length}</Badge>
            )}
          </div>
          {task.description && <div className="text-xs text-slate-500 mt-1">{task.description}</div>}

          {open && (
            <div className="mt-3 space-y-3">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-mono uppercase text-slate-500">Status:</span>
                {["todo", "in_progress", "done"].map((s) => (
                  <button
                    key={s}
                    onClick={() => onStatus(s)}
                    data-testid={`task-status-${task.id}-${s}`}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium border ${task.status === s ? statusStyles[s] : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                  >{s.replace("_", " ")}</button>
                ))}
              </div>

              <div>
                <div className="text-[10px] font-mono uppercase text-slate-500 mb-1 flex items-center gap-1"><Paperclip className="h-3 w-3" /> Attachments</div>
                {(task.attachments || []).length === 0 && <div className="text-xs text-slate-400 mb-2">No attachments yet.</div>}
                <ul className="space-y-1 mb-2">
                  {(task.attachments || []).map((a) => (
                    <li key={a.id} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded px-2 py-1">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/marketing/attachments/${a.id}/download`}
                        target="_blank" rel="noreferrer"
                        className="text-xs text-blue-700 hover:underline truncate flex-1 flex items-center gap-1"
                      >
                        <Download className="h-3 w-3" /> {a.filename}
                        <span className="text-[10px] text-slate-400 ml-2">{Math.ceil((a.size || 0) / 1024)}KB</span>
                      </a>
                      <button className="text-slate-400 hover:text-rose-600 ml-2" onClick={() => onRemoveAtt(a.id)} data-testid={`remove-att-${a.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
                <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer bg-white border border-slate-200 hover:border-blue-400 rounded px-2 py-1">
                  <Paperclip className="h-3 w-3" /> Add file
                  <input type="file" multiple hidden onChange={(e) => onUpload(e.target.files)} data-testid={`upload-${task.id}`} />
                </label>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-start gap-0.5">
          <div className="flex flex-col -space-y-1 mr-1" data-testid={`task-reorder-${task.id}`}>
            <button
              className="text-slate-400 hover:text-blue-600 disabled:text-slate-200 disabled:cursor-not-allowed p-0.5"
              onClick={onMoveUp}
              disabled={isFirst}
              aria-label="Move task up"
              title="Move up"
              data-testid={`move-up-${task.id}`}
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button
              className="text-slate-400 hover:text-blue-600 disabled:text-slate-200 disabled:cursor-not-allowed p-0.5"
              onClick={onMoveDown}
              disabled={isLast}
              aria-label="Move task down"
              title="Move down"
              data-testid={`move-down-${task.id}`}
            >
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
          </div>
          <button className="text-slate-400 hover:text-blue-600 p-1" onClick={onEdit} data-testid={`edit-task-${task.id}`}>
            <Pencil className="h-4 w-4" />
          </button>
          <button className="text-slate-400 hover:text-rose-600 p-1" onClick={onDelete} data-testid={`delete-task-${task.id}`}>
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function OwnerSelect({ users, value, onChange, testid, compact = false }) {
  return (
    <div>
      {!compact && <label className="text-[10px] font-mono uppercase text-slate-500">Owner</label>}
      <Select value={value || "unassigned"} onValueChange={(v) => onChange(v === "unassigned" ? "" : v)}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder="Unassigned" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="unassigned">Unassigned</SelectItem>
          {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name || u.email}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

// -----------------------------------------------------------------------------
// CALENDAR
// -----------------------------------------------------------------------------
function CalendarPanel() {
  const [view, setView] = useState("month");
  const [anchor, setAnchor] = useState(new Date()); // reference date
  const [activities, setActivities] = useState([]);
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);

  // Compute range based on view
  const range = useMemo(() => computeRange(anchor, view), [anchor, view]);

  const reload = async () => {
    const [a, u] = await Promise.all([
      api.get(`/marketing/activities?start=${range.startIso}&end=${range.endIso}`),
      api.get("/users"),
    ]);
    setActivities(a.data); setUsers(u.data);
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [range.startIso, range.endIso]);

  const shift = (dir) => {
    const d = new Date(anchor);
    if (view === "month") d.setMonth(d.getMonth() + dir);
    else if (view === "quarter") d.setMonth(d.getMonth() + dir * 3);
    else d.setFullYear(d.getFullYear() + dir);
    setAnchor(d);
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this activity?")) return;
    try { await api.delete(`/marketing/activities/${id}`); await reload(); }
    catch (_) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5" data-testid="marketing-calendar">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => shift(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-heading text-lg text-slate-900 min-w-[210px] text-center" data-testid="cal-range-label">
            {range.label}
          </div>
          <Button variant="outline" size="icon" onClick={() => shift(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="ghost" size="sm" onClick={() => setAnchor(new Date())}>Today</Button>
        </div>
        <div className="flex items-center gap-2">
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="month" data-testid="cal-view-month">Month</TabsTrigger>
              <TabsTrigger value="quarter" data-testid="cal-view-quarter">Quarter</TabsTrigger>
              <TabsTrigger value="year" data-testid="cal-view-year">Year</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button onClick={() => setCreating(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-activity">
            <Plus className="h-4 w-4 mr-1" /> Add activity
          </Button>
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-2">
        {ACTIVITY_TYPES.map((t) => {
          const Icon = t.icon;
          return (
            <span key={t.value} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${t.color}`}>
              <Icon className="h-3 w-3" /> {t.label}
            </span>
          );
        })}
      </div>

      {view === "month" && <MonthGrid anchor={anchor} activities={activities} onClick={setEditing} />}
      {view === "quarter" && <QuarterView anchor={anchor} activities={activities} onClick={setEditing} />}
      {view === "year" && <YearView anchor={anchor} activities={activities} onClick={setEditing} />}

      {/* List view fallback */}
      <Card className="border-slate-200">
        <CardHeader className="border-b border-slate-100 py-3">
          <CardTitle className="text-sm font-heading flex items-center gap-2"><CalIcon className="h-3.5 w-3.5 text-slate-500" /> All in {range.label} ({activities.length})</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {activities.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">No activities in this range.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {activities.map((a) => {
                const m = typeMeta(a.activity_type);
                const Icon = m.icon;
                return (
                  <li key={a.id} className="px-4 py-2 flex items-center justify-between hover:bg-slate-50" data-testid={`activity-row-${a.id}`}>
                    <button onClick={() => setEditing(a)} className="flex-1 flex items-center gap-3 text-left">
                      <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-medium ${m.color}`}>
                        <Icon className="h-3 w-3" /> {m.label}
                      </span>
                      <span className="font-medium text-slate-900 text-sm">{a.title}</span>
                      <span className="text-xs text-slate-500">
                        {fmtDate(a.start_date)}{a.end_date && a.end_date !== a.start_date ? ` → ${fmtDate(a.end_date)}` : ""}
                        {activityTimeLabel(a) && <span className="ml-1.5 font-mono text-slate-600">· {activityTimeLabel(a)}</span>}
                      </span>
                      {a.owner_name && <Badge variant="outline" className="text-[10px]">{a.owner_name}</Badge>}
                    </button>
                    <button className="text-slate-400 hover:text-rose-600" onClick={() => remove(a.id)} data-testid={`delete-activity-${a.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      <ActivityDialog
        open={creating || !!editing}
        onClose={() => { setCreating(false); setEditing(null); }}
        initial={editing}
        users={users}
        afterSave={() => { setCreating(false); setEditing(null); reload(); }}
        onDeleted={() => { setCreating(false); setEditing(null); reload(); }}
      />
    </div>
  );
}

function ActivityDialog({ open, onClose, initial, users, afterSave, onDeleted }) {
  const [form, setForm] = useState({});
  const [deleting, setDeleting] = useState(false);
  useEffect(() => {
    if (initial) setForm({ ...initial, all_day: initial.all_day !== false && !initial.start_time });
    else setForm({
      title: "", activity_type: "webinar",
      start_date: new Date().toISOString().slice(0, 10), end_date: "",
      all_day: true, start_time: "", end_time: "",
      description: "", owner_id: "", location: "", link: "",
    });
  }, [initial, open]);

  const save = async () => {
    if (!form.title?.trim()) return toast.error("Title required");
    if (!form.start_date) return toast.error("Start date required");
    if (!form.all_day && !form.start_time) return toast.error("Start time required when not all-day");
    try {
      const payload = {
        title: form.title.trim(),
        activity_type: form.activity_type,
        start_date: form.start_date,
        end_date: form.end_date || null,
        all_day: !!form.all_day,
        start_time: form.all_day ? null : (form.start_time || null),
        end_time: form.all_day ? null : (form.end_time || null),
        description: (form.description || "").trim() || null,
        owner_id: form.owner_id || null,
        location: (form.location || "").trim() || null,
        link: (form.link || "").trim() || null,
      };
      if (initial?.id) await api.patch(`/marketing/activities/${initial.id}`, payload);
      else await api.post("/marketing/activities", payload);
      afterSave();
      toast.success(initial?.id ? "Activity updated" : "Activity created");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    if (!initial?.id) return;
    if (!window.confirm(`Delete “${initial.title}”? This can't be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/marketing/activities/${initial.id}`);
      toast.success("Activity deleted");
      onDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{initial?.id ? "Edit activity" : "Add marketing activity"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Title *</label>
            <Input data-testid="activity-title" value={form.title || ""} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Cyber-safe schools webinar" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Type *</label>
            <Select value={form.activity_type || "webinar"} onValueChange={(v) => setForm({ ...form, activity_type: v })}>
              <SelectTrigger data-testid="activity-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ACTIVITY_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">Start date *</label>
              <Input data-testid="activity-start" type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div>
              <label className="text-[10px] font-mono uppercase text-slate-500">End date</label>
              <Input data-testid="activity-end" type="date" value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
            </div>
          </div>
          <div className="flex items-center gap-2 bg-slate-50 rounded p-2">
            <input
              id="all-day-toggle"
              type="checkbox"
              className="h-4 w-4 accent-blue-600"
              checked={!!form.all_day}
              onChange={(e) => setForm({ ...form, all_day: e.target.checked })}
              data-testid="activity-all-day"
            />
            <label htmlFor="all-day-toggle" className="text-xs text-slate-700 cursor-pointer select-none">
              All-day (no specific time)
            </label>
          </div>
          {!form.all_day && (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-mono uppercase text-slate-500">Start time *</label>
                <Input
                  data-testid="activity-start-time"
                  type="time"
                  value={form.start_time || ""}
                  onChange={(e) => setForm({ ...form, start_time: e.target.value })}
                />
              </div>
              <div>
                <label className="text-[10px] font-mono uppercase text-slate-500">End time</label>
                <Input
                  data-testid="activity-end-time"
                  type="time"
                  value={form.end_time || ""}
                  onChange={(e) => setForm({ ...form, end_time: e.target.value })}
                />
              </div>
            </div>
          )}
          <OwnerSelect users={users} value={form.owner_id || ""} onChange={(v) => setForm({ ...form, owner_id: v })} />
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Location</label>
            <Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="e.g. NEC Birmingham" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Link</label>
            <Input value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" />
          </div>
          <div>
            <label className="text-[10px] font-mono uppercase text-slate-500">Notes</label>
            <Textarea rows={3} value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
        </div>
        <DialogFooter className="sm:justify-between">
          {initial?.id ? (
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={deleting}
              className="text-rose-600 border-rose-200 hover:bg-rose-50 hover:text-rose-700"
              data-testid="activity-delete"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {deleting ? "Deleting…" : "Delete"}
            </Button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={save} data-testid="activity-save" className="bg-blue-600 hover:bg-blue-700">
              {initial?.id ? "Save" : "Create"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -----------------------------------------------------------------------------
// Calendar helpers
// -----------------------------------------------------------------------------
function computeRange(anchor, view) {
  // Xentra runs on UK FY (April → March). Q1=Apr-Jun, Q2=Jul-Sep, Q3=Oct-Dec, Q4=Jan-Mar.
  const y = anchor.getFullYear();
  const m = anchor.getMonth(); // 0-based
  let start, end, label;
  if (view === "month") {
    start = new Date(y, m, 1);
    end = new Date(y, m + 1, 0);
    label = start.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  } else if (view === "quarter") {
    // Fiscal quarter
    let fyStartYear, qIdx;
    if (m >= 3) { // Apr(=3) onwards
      fyStartYear = y;
      qIdx = Math.floor((m - 3) / 3) + 1; // Apr-Jun→1, Jul-Sep→2, Oct-Dec→3
    } else {
      fyStartYear = y - 1;
      qIdx = 4; // Jan-Mar
    }
    const qStartMonthAbs = 3 + (qIdx - 1) * 3; // 3, 6, 9, 12 (in Apr-based zero index terms it's 3-based)
    const startYear = fyStartYear + (qStartMonthAbs > 11 ? 1 : 0);
    const startMonth = qStartMonthAbs > 11 ? qStartMonthAbs - 12 : qStartMonthAbs;
    start = new Date(startYear, startMonth, 1);
    end = new Date(startYear, startMonth + 3, 0);
    const fyEndYearShort = String(fyStartYear + 1).slice(-2);
    label = `FY${fyEndYearShort} Q${qIdx} · ${start.toLocaleDateString("en-GB", { month: "short" })}–${end.toLocaleDateString("en-GB", { month: "short" })}`;
  } else {
    // Fiscal year: April (year start) → March (year+1)
    const fyStartYear = m >= 3 ? y : y - 1;
    start = new Date(fyStartYear, 3, 1);
    end = new Date(fyStartYear + 1, 2, 31);
    const fyEndYearShort = String(fyStartYear + 1).slice(-2);
    label = `FY${fyEndYearShort} · Apr ${fyStartYear} – Mar ${fyStartYear + 1}`;
  }
  const toIso = (d) => d.toISOString().slice(0, 10);
  return { start, end, startIso: toIso(start), endIso: toIso(end), label };
}

function activitiesOnDate(activities, d) {
  const iso = d.toISOString().slice(0, 10);
  return activities.filter((a) => {
    const s = a.start_date;
    const e = a.end_date || a.start_date;
    return s <= iso && iso <= e;
  });
}

function MonthGrid({ anchor, activities, onClick }) {
  const y = anchor.getFullYear(); const m = anchor.getMonth();
  const first = new Date(y, m, 1);
  const startDow = (first.getDay() + 6) % 7; // Monday-first
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(y, m, d));
  while (cells.length % 7 !== 0) cells.push(null);
  const today = new Date().toISOString().slice(0, 10);
  return (
    <Card className="border-slate-200">
      <CardContent className="p-3">
        <div className="grid grid-cols-7 gap-1 mb-1 text-[10px] font-mono uppercase tracking-wider text-slate-400">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="px-1">{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c) return <div key={i} className="min-h-[92px] bg-slate-50/50 rounded" />;
            const iso = c.toISOString().slice(0, 10);
            const acts = activitiesOnDate(activities, c);
            const isToday = iso === today;
            return (
              <div key={i} className={`min-h-[92px] bg-white border rounded p-1.5 space-y-1 ${isToday ? "border-blue-400 ring-1 ring-blue-100" : "border-slate-200"}`}>
                <div className={`text-[10px] font-mono ${isToday ? "text-blue-700 font-bold" : "text-slate-500"}`}>{c.getDate()}</div>
                {acts.slice(0, 3).map((a) => {
                  const meta = typeMeta(a.activity_type);
                  const Icon = meta.icon;
                  const t = activityTimeLabel(a);
                  return (
                    <button
                      key={a.id}
                      onClick={() => onClick(a)}
                      className={`w-full text-left rounded px-1 py-0.5 text-[10px] flex items-center gap-1 border ${meta.color} truncate`}
                      title={`${a.title}${t ? ` · ${t}` : ""}`}
                      data-testid={`cal-cell-activity-${a.id}`}
                    >
                      <Icon className="h-2.5 w-2.5 flex-shrink-0" />
                      {t && <span className="font-mono text-[9px] opacity-80 flex-shrink-0">{t.split("–")[0]}</span>}
                      <span className="truncate">{a.title}</span>
                    </button>
                  );
                })}
                {acts.length > 3 && <div className="text-[9px] text-slate-500">+{acts.length - 3} more</div>}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

function QuarterView({ anchor, activities, onClick }) {
  // Fiscal quarter: derive the 3 months of the current fiscal quarter based on anchor
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  let fyStartYear, qIdx;
  if (m >= 3) { fyStartYear = y; qIdx = Math.floor((m - 3) / 3) + 1; }
  else { fyStartYear = y - 1; qIdx = 4; }
  const qStartMonthAbs = 3 + (qIdx - 1) * 3;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {[0, 1, 2].map((i) => {
        const abs = qStartMonthAbs + i;
        const monthAnchor = new Date(fyStartYear + (abs > 11 ? 1 : 0), abs > 11 ? abs - 12 : abs, 1);
        return <MiniMonth key={i} anchor={monthAnchor} activities={activities} onClick={onClick} />;
      })}
    </div>
  );
}

function YearView({ anchor, activities, onClick }) {
  // Fiscal year: Apr(year) → Mar(year+1)
  const y = anchor.getFullYear();
  const m = anchor.getMonth();
  const fyStartYear = m >= 3 ? y : y - 1;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {Array.from({ length: 12 }, (_, i) => {
        const abs = 3 + i; // April = month index 3
        const monthAnchor = new Date(fyStartYear + (abs > 11 ? 1 : 0), abs > 11 ? abs - 12 : abs, 1);
        return <MiniMonth key={i} anchor={monthAnchor} activities={activities} onClick={onClick} />;
      })}
    </div>
  );
}

function MiniMonth({ anchor, activities, onClick }) {
  const y = anchor.getFullYear(); const m = anchor.getMonth();
  const inMonth = activities.filter((a) => {
    const s = new Date(a.start_date);
    const e = new Date(a.end_date || a.start_date);
    const monthEnd = new Date(y, m + 1, 0);
    const monthStart = new Date(y, m, 1);
    return e >= monthStart && s <= monthEnd;
  }).sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  return (
    <Card className="border-slate-200">
      <CardHeader className="border-b border-slate-100 py-2">
        <CardTitle className="text-xs font-heading">{anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</CardTitle>
      </CardHeader>
      <CardContent className="p-2">
        {inMonth.length === 0 ? (
          <div className="text-[10px] text-slate-400 text-center py-3">None</div>
        ) : (
          <ul className="space-y-1">
            {inMonth.map((a) => {
              const meta = typeMeta(a.activity_type);
              const Icon = meta.icon;
              const t = activityTimeLabel(a);
              return (
                <li key={a.id}>
                  <button
                    onClick={() => onClick(a)}
                    className={`w-full text-left rounded px-2 py-1 text-[11px] flex items-center gap-1.5 border ${meta.color}`}
                  >
                    <Icon className="h-3 w-3 flex-shrink-0" />
                    <span className="truncate flex-1">{a.title}</span>
                    <span className="text-[9px] font-mono opacity-70">
                      {a.start_date?.slice(5)}{t ? ` ${t.split("–")[0]}` : ""}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
